import React, { useState, useEffect, useRef } from "react";
import { DateRangePicker } from "react-date-range";
import { format } from "date-fns";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogContentText,
  FormControlLabel,
  TextField,
} from "@mui/material";
import TodayIcon from "@mui/icons-material/Today";
import dayjs from "dayjs";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css"; // theme css file
import { clampRangeToMaxDays } from "../../utils/dateformat/dateFormat";

interface Props {
  startDate: Date;
  endDate: Date;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  /**
   * 선택 가능한 최대 기간(달력일, 양끝 포함). 주면 시작일을 고른 뒤 그 범위 밖의
   * 날짜는 달력에서 비활성화되고, 직접 입력·프리셋으로 넘긴 범위는 끝 기준으로
   * 시작을 당겨 맞춘다. 없으면 기존처럼 제한 없음.
   */
  maxRangeDays?: number;
}

const DateRangeComponent = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  maxRangeDays,
}: Props) => {
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState([
    {
      startDate: new Date(startDate),
      endDate,
      key: "selection",
    },
  ]);
  const [rangeDate, setRangeDate] = useState("");
  // react-date-range 의 선택 단계. [0,0] = 시작일 고르는 중, [0,1] = 끝일 고르는 중.
  // 주의: 라이브러리 DateRangePicker 는 내부 onRangeFocusChange 뒤에 {...props} 를
  // 펼치므로, onRangeFocusChange 를 넘기는 순간 내부 상태 갱신이 끊긴다. 그래서
  // focusedRange 도 함께 넘겨 완전히 제어(controlled) 모드로 쓴다.
  const [focusedRange, setFocusedRange] = useState<[number, 0 | 1]>([0, 0]);
  const nowDate = dayjs().toDate();

  // 끝일을 고르는 동안에는 시작일 ± (maxRangeDays-1) 바깥을 비활성화한다.
  // (두 번째 클릭이 시작일보다 앞이면 라이브러리가 시작/끝을 뒤바꾸므로 양쪽 다 막는다)
  const pickingEnd = !!maxRangeDays && focusedRange[1] === 1;
  const anchor = dayjs(state[0].startDate);
  const span = (maxRangeDays ?? 1) - 1;
  const minDate = pickingEnd ? anchor.subtract(span, "day").toDate() : undefined;
  const maxDate = pickingEnd
    ? new Date(Math.min(anchor.add(span, "day").valueOf(), nowDate.valueOf()))
    : nowDate;

  const applySelection = (selection: any) => {
    if (!maxRangeDays) return selection;
    const clamped = clampRangeToMaxDays(
      selection.startDate,
      selection.endDate,
      maxRangeDays
    );
    return { ...selection, ...clamped };
  };

  const closeCal = (event: any) => {
    if (
      calendarRef.current &&
      !calendarRef.current?.contains(event.target) &&
      inputRef.current &&
      !inputRef.current?.contains(event.target)
    ) {
      setIsOpen(false);
    }
  };
  const handleTextFieldClick = () => {
    setIsOpen(true);
  };
  const onSetRangeValue = () => {
    const start = format(state[0].startDate, "yyyy/MM/dd");
    const end = format(state[0].endDate, "yyyy/MM/dd");
    setRangeDate(`${start} ~ ${end}`);
  };

  useEffect(() => {
    onSetRangeValue();
    setStartDate(state[0].startDate);
    setEndDate(state[0].endDate);
  }, [state]);

  useEffect(() => {
    document.addEventListener("click", closeCal);
    return () => {
      document.removeEventListener("click", closeCal);
    };
  }, []);

  return (
    <div style={{ position: "relative", zIndex: 2 }}>
      <TextField
        style={{ position: "relative", zIndex: 2, width: 260 }}
        onClick={handleTextFieldClick}
        ref={inputRef}
        size="small"
        label="Date"
        value={rangeDate}
        InputProps={{
          endAdornment: <TodayIcon />,
        }}
      />

      <div
        ref={calendarRef}
        className={isOpen ? "open-cal" : ""}
        style={{
          display: "block",
          marginTop: "15px",
          width: "450px",
        }}
      >
        <DateRangePicker
          minDate={minDate}
          maxDate={maxDate}
          editableDateInputs={true}
          onChange={(item: any) => {
            setState([applySelection(item.selection)]);
          }}
          focusedRange={focusedRange}
          onRangeFocusChange={(range: any) => setFocusedRange(range)}
          moveRangeOnFirstSelection={false}
          ranges={state}
          months={2}
          direction="horizontal"
        />
      </div>
    </div>
  );
};

export default DateRangeComponent;
